/**
 * Botão de inserção de variáveis — wrapper que abre o novo VariableExplorerModal.
 *
 * Mantém a API pública anterior (textareaRef + onInsert OU onSelectVariable)
 * para retrocompatibilidade com todos os pontos que já consomem este botão.
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-07 v2.1.0 - Adicionado modo explícito para destinos graváveis.
 *   RELEASE: 2026-01-01 v2.0.0 - Substituído popover por modal categorizado.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Braces } from 'lucide-react';
import { cn } from '@/lib/utils';
import { VariableExplorerModal } from './variables/VariableExplorerModal';
import type { TenantVariable } from '@/hooks/useTenantVariables';
import { VARIABLE_CATALOG_BY_TOKEN } from '@/lib/flow/variableCatalog';

interface VariableInsertButtonProps {
  tenantId: string;
  // Modo textarea (original)
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  onInsert?: (newValue: string) => void;
  // Modo callback genérico
  onSelectVariable?: (variable: TenantVariable) => void;
  // Customização visual
  variant?: 'icon' | 'button';
  buttonLabel?: string;
  className?: string;
  /** Filtrar apenas variáveis não-sistema (customizadas pelo cliente) */
  excludeSystem?: boolean;
  /** Define se o modal insere tokens ou seleciona uma variável gravável. */
  mode?: 'insert' | 'destination';
}

/**
 * Constrói um "TenantVariable-like" a partir de um token do catálogo,
 * para alimentar o callback `onSelectVariable` mantendo retrocompatibilidade.
 */
function buildVariableFromToken(token: string): TenantVariable {
  const catalog = VARIABLE_CATALOG_BY_TOKEN[token];
  return {
    id: `catalog_${token}`,
    tenant_id: '',
    name: token,
    display_name: catalog?.label || token,
    type: (catalog?.type as TenantVariable['type']) || 'text',
    description: catalog?.description || null,
    default_value: catalog?.example || null,
    is_system: catalog?.source !== 'custom',
    category: catalog?.category || 'custom',
    created_by: null,
    created_at: '',
    updated_at: '',
  };
}

export function VariableInsertButton({
  tenantId,
  textareaRef,
  onInsert,
  onSelectVariable,
  variant = 'icon',
  buttonLabel = 'Inserir Variável',
  className,
  excludeSystem = false,
  mode = 'insert',
}: VariableInsertButtonProps) {
  const [open, setOpen] = useState(false);

  /**
   * Handler unificado: recebe o token e propaga conforme o modo do consumidor.
   */
  const handleInsert = (token: string) => {
    // Modo callback genérico tem prioridade
    if (onSelectVariable) {
      onSelectVariable(buildVariableFromToken(token));
      return;
    }

    // Modo textarea
    const textarea = textareaRef?.current;
    if (!textarea || !onInsert) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = textarea.value;
    const insertText = `{{${token}}}`;
    const newValue = currentValue.substring(0, start) + insertText + currentValue.substring(end);

    onInsert(newValue);

    // Reposiciona cursor após inserção
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + insertText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <>
      {variant === 'icon' ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8 flex-shrink-0', className)}
          title="Inserir variável"
          onClick={() => setOpen(true)}
        >
          <Braces className="h-4 w-4" />
        </Button>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            'text-purple-600 dark:text-purple-400 border-purple-300 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20',
            className
          )}
          onClick={() => setOpen(true)}
        >
          <Braces className="h-3 w-3 mr-1" />
          {buttonLabel}
        </Button>
      )}

      <VariableExplorerModal
        open={open}
        onOpenChange={setOpen}
        tenantId={tenantId}
        onInsert={mode === 'insert' ? handleInsert : () => undefined}
        excludeSystem={excludeSystem}
        mode={mode}
        onSelect={mode === 'destination' ? onSelectVariable : undefined}
      />
    </>
  );
}
